/**
 * PpiInsufficientBanner — the dossier's honest empty state for the supplier the
 * PPI engine can never speak for (Lane 718, Ryan's D52.2).
 *
 * Ryan: *"For suppliers where there is not enough data for PPI, there should be
 * a banner in that section on their dossier saying not enough spend info
 * available for PPI comparison."* → "That's fine." (register §9.2)
 *
 * ⚠️ THERE ARE TWO SILENCES ON THIS PANEL AND THEY HAVE OPPOSITE REMEDIES.
 * Collapsing them is the one way this banner makes the page worse:
 *
 *   1. NO PPI PROFILE CONFIGURED — nobody has mapped this supplier to a market
 *      index. FIXABLE: an admin maps it and a number appears. The server sends
 *      `ppi_blend: null`, SupplierDetail renders its own "PPI benchmark profile
 *      not yet configured" panel instead of the card, and PriceSummaryBand's
 *      trend tiles say "No market benchmark configured for this supplier."
 *      THIS COMPONENT IS NEVER MOUNTED IN THAT STATE — the distinction is
 *      structural (a different branch of a different panel), not a matter of
 *      wording that a later edit could blur.
 *
 *   2. NOT ENOUGH PURCHASE HISTORY — the benchmark IS mapped; the engine
 *      refuses because the purchase orders underneath it cannot support a price
 *      change. NOT FIXABLE by any mapping, at any spend floor. This banner.
 *
 * Telling silence 2 that an admin can set something up is telling a buyer to
 * wait for something that is never coming, and the population is real and
 * permanent — seven suppliers over $95K of trailing-12-month spend on staging
 * after the 2026-08-02 $50K mapping run (Tetrachem Seal $324,814 down to Gavco
 * Plastics $95,090; register §9.2).
 *
 * ⚠️ THE CONDITION IS THE ENGINE'S OWN VERDICT, NOT A PROXY WE INVENTED.
 * `ppi_blend.confidence === 'insufficient'` is the single sentinel BOTH bases
 * terminate in — the legacy basis when every material falls under
 * `analytics._PPI_BLEND_MIN_POS_PER_MATERIAL = 3`, and the live point-to-point
 * basis when `_point_to_point_compute` measures nothing (its own >= 2
 * guard-kept-PO test per material). Nine consumers already branch on it. This
 * component re-derives NOTHING: it does not count POs, does not read a spend
 * floor, and does not know what 3 is. If the floor moves, this banner moves
 * with it for free.
 *
 * ⚠️ AND THERE IS A THIRD SILENCE THE ENGINE DISTINGUISHES BUT THE EYE DOES
 * NOT. On the point-to-point basis `unmeasurable_reason` separates
 * `no_measurable_materials` / `no_matched_materials` (OUR purchase history —
 * what this banner says) from `no_market_window` (the INDEX has no data for the
 * window — nothing to do with our orders). Claiming the first when the truth is
 * the second is the same class of error as collapsing silences 1 and 2, so the
 * reason allowlist below is CLOSED: an unrecognised reason renders NO banner
 * and the page keeps today's per-window line. A future engine reason therefore
 * fails closed instead of being silently narrated as a purchase-history gap.
 */
import React from 'react';

/**
 * Engine reasons this banner's sentence is TRUE for.
 *
 * `undefined` is deliberately in the set: the legacy basis records no reason at
 * all, and both of its insufficient branches ARE purchase-history branches
 * (every material under the 3-PO floor, or no usable chart data from those
 * materials). PROD runs the legacy basis today — PPI_POINT_TO_POINT_ENABLED is
 * absent there — so excluding the missing reason would make this banner dark on
 * the one environment Ryan's customers use.
 */
const HISTORY_REASONS = new Set([
  'no_measurable_materials',
  'no_matched_materials',
]);

function isHistoryRefusal(blend) {
  if (!blend || blend.confidence !== 'insufficient') return false;
  const reason = blend.unmeasurable_reason;
  if (reason === undefined || reason === null || reason === '') return true;
  return HISTORY_REASONS.has(reason);
}

/**
 * Props:
 *   blends — the blend object for EVERY window the panel renders, in order
 *            (6/12/24), with null/undefined for a window still in flight.
 *
 * Renders nothing unless every one of those windows has arrived AND every one
 * is a purchase-history refusal:
 *
 *   - a window still loading  -> nothing. Announcing "there is no comparison"
 *     while a comparison is still being fetched is a lie with a 300ms lifetime,
 *     and this panel fires three requests.
 *   - ANY window with a real figure -> nothing. A panel-wide "we cannot compare
 *     this supplier" over a card showing a gap of +8.0% is self-contradiction;
 *     the per-window "Insufficient purchase history for this window" line
 *     already covers the partial case and stays exactly as it is.
 */
export default function PpiInsufficientBanner({ blends }) {
  const windows = Array.isArray(blends) ? blends : [];
  if (windows.length === 0) return null;
  if (!windows.every(Boolean)) return null;
  if (!windows.every(isHistoryRefusal)) return null;

  return (
    <div className="sd-grain" data-testid="ppi-insufficient-banner">
      <b>Not enough purchase history for a market comparison.</b>{' '}
      The market benchmark for this supplier is set up. What is missing is
      repeat orders. Measuring a price change needs at least three orders of the
      same material inside a window, and none of the windows here reaches that.
    </div>
  );
}
